import { z } from 'zod';
/**
 * Trainingsvideos an Uebungen und Hinweise zu Uebungen.
 *
 * - Eine eigene Uebung hat 0 bis 3 Videos (Handyaufnahme oder Datei). Katalog-
 *   und Linkuebungen haben keine.
 * - Groesse, Laenge und Gesamtspeicher sind je Verein einstellbar, innerhalb
 *   fester Grenzen. Der Server prueft die Groesse streamend und das Kontingent;
 *   die Laenge kennt nur der Client (kein ffmpeg auf dem Server).
 * - Hinweise sind Rueckmeldungen zu einer Uebung (Katalog oder eigene) an die
 *   Verantwortlichen der Trainingsplanung.
 */
const trimmed = (max) => z.string().trim().max(max);
/** Erlaubte Containerformate; geprueft wird MIME-Typ UND Dateisignatur. */
export const TRAINING_VIDEO_MIME = ['video/mp4', 'video/webm', 'video/quicktime'];
export const TRAINING_VIDEO_FORMAT_LABEL = 'MP4 (H.264), WebM oder MOV';
export const TRAINING_VIDEO_LIMITS = {
    /** Videos je Uebung. */
    perExercise: 3,
    title: 100,
    /** Einstellbar: Obergrenze fuer die Dateigroesse eines Videos in MB. */
    sizeMb: { min: 5, max: 500, default: 100 },
    /** Einstellbar: Obergrenze fuer die Laenge eines Videos in Sekunden. */
    durationSeconds: { min: 10, max: 600, default: 120 },
    /** Einstellbar: Gesamtspeicher aller Trainingsvideos eines Vereins in GB. */
    quotaGb: { min: 1, max: 200, default: 10 },
};
export const BYTES_PER_MB = 1024 * 1024;
export const BYTES_PER_GB = 1024 * BYTES_PER_MB;
const intInRange = (range, label, unit) => z.number({ invalid_type_error: `${label}: bitte eine Zahl angeben` })
    .int(`${label}: ganze Zahl in ${unit}`)
    .min(range.min, `${label}: mindestens ${range.min} ${unit}`)
    .max(range.max, `${label}: höchstens ${range.max} ${unit}`);
/** Einstellungen eines Vereins fuer Trainingsvideos. */
export const trainingVideoSettingsSchema = z.object({
    maxSizeMb: intInRange(TRAINING_VIDEO_LIMITS.sizeMb, 'Maximale Videogröße', 'MB'),
    maxDurationSeconds: intInRange(TRAINING_VIDEO_LIMITS.durationSeconds, 'Maximale Videolänge', 'Sekunden'),
    quotaGb: intInRange(TRAINING_VIDEO_LIMITS.quotaGb, 'Speicher für Trainingsvideos', 'GB'),
});
export const DEFAULT_TRAINING_VIDEO_SETTINGS = {
    maxSizeMb: TRAINING_VIDEO_LIMITS.sizeMb.default,
    maxDurationSeconds: TRAINING_VIDEO_LIMITS.durationSeconds.default,
    quotaGb: TRAINING_VIDEO_LIMITS.quotaGb.default,
};
/** Titel aendern. */
export const trainingVideoUpdateSchema = z.object({
    title: trimmed(TRAINING_VIDEO_LIMITS.title),
});
/** Neue Reihenfolge: alle Videos der Uebung, in der gewuenschten Reihenfolge. */
export const trainingVideoOrderSchema = z.object({
    ids: z.array(z.string().min(1)).max(TRAINING_VIDEO_LIMITS.perExercise),
});
/** Zahl aus einem Formularfeld; leer oder unlesbar ergibt null (die Angabe ist freiwillig). */
const optionalNumber = (min, max, whole) => z.preprocess((value) => (value === '' || value === undefined || value === null ? null : Number(value)), z.number().finite().min(min).max(max).nullable()).catch(null).transform((value) => (value === null ? null : whole ? Math.round(value) : value));
/** Optionale Angaben des Clients zum Video (Formularfelder vor der Datei). */
export const trainingVideoMetaSchema = z.object({
    title: trimmed(TRAINING_VIDEO_LIMITS.title).default(''),
    durationSeconds: optionalNumber(0, 24 * 3600, true),
    width: optionalNumber(1, 20000, true),
    height: optionalNumber(1, 20000, true),
});
/** "240 MB", "1,5 GB": Groessen fuer Meldungen, auf Deutsch gerundet. */
export function formatBytes(bytes) {
    if (bytes >= BYTES_PER_GB)
        return `${(bytes / BYTES_PER_GB).toLocaleString('de-AT', { maximumFractionDigits: 1 })} GB`;
    if (bytes >= BYTES_PER_MB)
        return `${Math.round(bytes / BYTES_PER_MB).toLocaleString('de-AT')} MB`;
    if (bytes >= 1024)
        return `${Math.round(bytes / 1024).toLocaleString('de-AT')} KB`;
    return `${bytes} Byte`;
}
/** "1:35" oder "2 Min. 5 Sek." fuer Meldungen. */
export function formatDuration(seconds) {
    const total = Math.max(0, Math.round(seconds));
    const minutes = Math.floor(total / 60);
    const rest = total % 60;
    return minutes === 0 ? `${rest} Sek.` : `${minutes} Min. ${rest} Sek.`;
}
/** Meldung fuer ein zu grosses Video; `size` ist die tatsaechliche Groesse, falls bekannt. */
export function videoTooLargeMessage(maxMb, sizeBytes) {
    const was = sizeBytes ? `Das Video ist ${formatBytes(sizeBytes)} groß, erlaubt sind ${maxMb} MB` : `Das Video ist größer als die erlaubten ${maxMb} MB`;
    return `${was} – am Handy in 720p aufnehmen oder kürzen.`;
}
/** Meldung fuer ein zu langes Video. */
export function videoTooLongMessage(maxSeconds, seconds) {
    return `Das Video ist ${formatDuration(seconds)} lang, erlaubt sind ${formatDuration(maxSeconds)} – bitte kürzen.`;
}
/* ------------------------------------------------------------------ *
 * Hinweise zu Uebungen
 * ------------------------------------------------------------------ */
export const EXERCISE_REMARK_STATUS = ['OPEN', 'DONE'];
export const EXERCISE_REMARK_STATUS_LABELS = {
    OPEN: 'Offen',
    DONE: 'Erledigt',
};
export const EXERCISE_REMARK_LIMITS = { text: 2000, answer: 2000 };
export const exerciseRemarkInputSchema = z.object({
    text: z.string().trim().min(1, 'Bitte schreibe einen Hinweis').max(EXERCISE_REMARK_LIMITS.text, `Höchstens ${EXERCISE_REMARK_LIMITS.text} Zeichen`),
});
export const exerciseRemarkResolveSchema = z.object({
    answer: z.string().trim().max(EXERCISE_REMARK_LIMITS.answer, `Höchstens ${EXERCISE_REMARK_LIMITS.answer} Zeichen`).nullish().transform((value) => value || null),
});
export const exerciseRemarkListQuerySchema = z.object({
    status: z.preprocess((value) => (value === '' ? undefined : value), z.enum(EXERCISE_REMARK_STATUS).optional()),
});
//# sourceMappingURL=training-video.js.map